import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useQueryClient } from '@tanstack/react-query';
import type { JSONContent } from '@tiptap/react';
import { nip19 } from 'nostr-tools';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSpace } from '@/hooks/useSpaces';
import { useSpacePages } from '@/hooks/usePages';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useDeleteEvent } from '@/hooks/useDeleteEvent';
import { useEditorStore } from '@/stores/editorStore';

import { SteleEditor } from '@/components/editor/SteleEditor';
import { PageTreeSidebar } from '@/components/spaces/PageTreeSidebar';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import {
  STELE_PAGE_KIND,
  STELE_SPACE_KIND,
  STELE_CLIENT,
  pageDTag,
  spaceCoordinate,
  getTagValue,
  parsePageTags,
  type PageEntry,
} from '@/lib/stele';
import { tiptapToMarkdown, markdownToTiptap } from '@/lib/markdown';
import { ArrowLeft, ArrowUpRight, Loader2, PanelLeftClose, PanelLeft, Save, Settings } from 'lucide-react';
import { SpaceSettingsDialog } from '@/components/spaces/SpaceSettingsDialog';
import { toast } from '@/hooks/useToast';

export function SpaceEditor() {
  const { spaceSlug, '*': pagePath } = useParams<{ spaceSlug: string; '*': string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { mutateAsync: deleteEvent } = useDeleteEvent();

  const { data: spaceEvent, isLoading: spaceLoading } = useSpace(user?.pubkey, spaceSlug);
  const { data: pageEvents, isLoading: pagesLoading } = useSpacePages(user?.pubkey, spaceSlug);

  const { isDirty, setDirty, sidebarCollapsed, toggleSidebar } = useEditorStore();

  const [activePageDTag, setActivePageDTag] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState<JSONContent | null>(null);
  const [pageTitle, setPageTitle] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const pageEntries = useMemo(() => {
    if (!spaceEvent) return [];
    return parsePageTags(spaceEvent.tags);
  }, [spaceEvent]);

  const pageTitles = useMemo(() => {
    const map = new Map<string, string>();
    if (pageEvents) {
      for (const ev of pageEvents) {
        const dTag = getTagValue(ev.tags, 'd');
        const title = getTagValue(ev.tags, 'title');
        if (dTag && title) map.set(dTag, title);
      }
    }
    return map;
  }, [pageEvents]);

  const activePageEvent = useMemo(() => {
    if (!activePageDTag || !pageEvents) return null;
    return pageEvents.find(e => getTagValue(e.tags, 'd') === activePageDTag) ?? null;
  }, [activePageDTag, pageEvents]);

  useEffect(() => {
    if (activePageEvent) {
      setPageTitle(getTagValue(activePageEvent.tags, 'title') ?? '');
      setEditorContent(
        activePageEvent.content
          ? markdownToTiptap(activePageEvent.content)
          : { type: 'doc', content: [{ type: 'paragraph' }] }
      );
      setDirty(false);
    } else if (activePageDTag) {
      setPageTitle('');
      setEditorContent({ type: 'doc', content: [{ type: 'paragraph' }] });
      setDirty(false);
    }
  }, [activePageEvent, activePageDTag, setDirty]);

  useEffect(() => {
    if (!pageEntries.length) return;
    if (pagePath) {
      const entry = pageEntries.find(e => e.path === '/' + pagePath);
      if (entry) { setActivePageDTag(entry.dTag); return; }
    }
    if (!activePageDTag) setActivePageDTag(pageEntries[0].dTag);
  }, [pageEntries, pagePath, activePageDTag]);

  const handleSelectPage = useCallback((dTag: string) => {
    const entry = pageEntries.find(e => e.dTag === dTag);
    if (entry && spaceSlug) {
      setActivePageDTag(dTag);
      navigate(`/s/${spaceSlug}${entry.path}`, { replace: true });
    }
  }, [pageEntries, spaceSlug, navigate]);

  const handleEditorUpdate = useCallback((json: JSONContent) => {
    setEditorContent(json);
    setDirty(true);
  }, [setDirty]);

  const handleSave = useCallback(async () => {
    if (!user || !spaceSlug || !activePageDTag || !editorContent) return;
    const markdown = tiptapToMarkdown(editorContent);
    try {
      await publishEvent({
        kind: STELE_PAGE_KIND,
        content: markdown,
        tags: [
          ['d', activePageDTag],
          ['title', pageTitle || 'Untitled'],
          ['a', spaceCoordinate(user.pubkey, spaceSlug)],
          ['summary', ''],
          ['alt', `Stele documentation page: ${pageTitle || 'Untitled'}`],
          ['client', STELE_CLIENT],
        ],
      });
      setDirty(false);
      await queryClient.invalidateQueries({ queryKey: ['nostr', 'stele'] });
      toast({ title: 'Saved', description: 'Published to your relays.' });
    } catch (err) {
      toast({ title: 'Save failed', description: String(err), variant: 'destructive' });
    }
  }, [user, spaceSlug, activePageDTag, editorContent, pageTitle, publishEvent, setDirty, queryClient]);

  const saveSpaceTree = useCallback(async (newEntries: PageEntry[]) => {
    if (!user || !spaceSlug || !spaceEvent) return;
    const pageTags = newEntries.map(e => ['page', e.dTag, e.path]);
    await publishEvent({
      kind: STELE_SPACE_KIND,
      content: '',
      tags: [...spaceEvent.tags.filter(t => t[0] !== 'page'), ...pageTags],
    });
    await queryClient.invalidateQueries({ queryKey: ['nostr', 'stele'] });
  }, [user, spaceSlug, spaceEvent, publishEvent, queryClient]);

  const handleCreatePage = useCallback(async (title: string, slug: string, parentPath?: string) => {
    if (!user || !spaceSlug) return;
    const dTag = pageDTag(slug);
    const path = parentPath ? `${parentPath}/${slug}` : `/${slug}`;
    await publishEvent({
      kind: STELE_PAGE_KIND,
      content: '',
      tags: [
        ['d', dTag], ['title', title],
        ['a', spaceCoordinate(user.pubkey, spaceSlug)],
        ['summary', ''],
        ['alt', `Stele documentation page: ${title}`],
        ['client', STELE_CLIENT],
      ],
    });
    await saveSpaceTree([...pageEntries, { dTag, path }]);
    setActivePageDTag(dTag);
    navigate(`/s/${spaceSlug}${path}`, { replace: true });
  }, [user, spaceSlug, pageEntries, publishEvent, saveSpaceTree, navigate]);

  const handleDeletePage = useCallback(async (dTag: string) => {
    if (!confirm('Delete this page? This cannot be undone.')) return;
    const pageEvent = pageEvents?.find(e => getTagValue(e.tags, 'd') === dTag);
    await deleteEvent({ kind: STELE_PAGE_KIND, dTag, eventId: pageEvent?.id });
    const newEntries = pageEntries.filter(e => e.dTag !== dTag);
    await saveSpaceTree(newEntries);
    if (newEntries.length > 0 && activePageDTag === dTag) handleSelectPage(newEntries[0].dTag);
    else if (newEntries.length === 0) setActivePageDTag(null);
  }, [pageEvents, pageEntries, deleteEvent, saveSpaceTree, activePageDTag, handleSelectPage]);

  const handleReorderPages = useCallback(async (newPages: PageEntry[]) => {
    await saveSpaceTree(newPages);
  }, [saveSpaceTree]);

  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); handleSave(); }
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [handleSave]);

  const spaceTitle = spaceEvent ? getTagValue(spaceEvent.tags, 'title') ?? 'Untitled' : '';
  const spaceIcon  = spaceEvent ? getTagValue(spaceEvent.tags, 'icon') ?? '📖' : '📖';
  const npub = user ? nip19.npubEncode(user.pubkey) : '';

  useSeoMeta({ title: spaceTitle ? `${spaceTitle} — Stele` : 'Stele' });

  if (!user) { navigate('/'); return null; }

  if (spaceLoading || pagesLoading) {
    return (
      <div className="h-screen flex bg-background">
        <div className="w-56 border-r border-border p-4 space-y-3">
          {[100, 80, 90, 70].map(w => <Skeleton key={w} className={`h-4 w-${w < 100 ? '[' + w + '%]' : 'full'}`} />)}
        </div>
        <div className="flex-1 p-10 space-y-4 max-w-2xl">
          <Skeleton className="h-9 w-64" />
          {[100, 90, 100, 70].map((w, i) => <Skeleton key={i} className="h-4 w-full" />)}
        </div>
      </div>
    );
  }

  if (!spaceEvent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-xs font-mono tracking-[0.2em] text-muted-foreground uppercase mb-4">Not Found</p>
          <h1 className="text-2xl font-bold mb-2">Space not found</h1>
          <p className="text-muted-foreground text-sm mb-6">
            "{spaceSlug}" could not be found on your relays.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>← Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">

      {/* ── Top bar ── */}
      <header className="flex items-center justify-between h-11 px-3 border-b border-border bg-background shrink-0">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => navigate('/')}>
            <ArrowLeft className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={toggleSidebar}>
            {sidebarCollapsed ? <PanelLeft className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
          </Button>
          <span className="text-base ml-1 select-none">{spaceIcon}</span>
          <span className="text-sm font-medium tracking-tight truncate ml-1 max-w-[180px]">{spaceTitle}</span>
          {isDirty && (
            <span className="ml-2 w-1.5 h-1.5 rounded-full bg-foreground/40 shrink-0" title="Unsaved changes" />
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant={isDirty ? 'default' : 'ghost'}
            size="sm"
            className="h-7 gap-1.5 text-xs font-mono tracking-wide uppercase px-3"
            onClick={handleSave}
            disabled={!isDirty || isPublishing}
          >
            {isPublishing
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Save className="h-3 w-3" />
            }
            Save
          </Button>
          <Button
            variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => navigate(`/r/${npub}/${spaceSlug}`)} title="View published"
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => setSettingsOpen(true)} title="Space settings"
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
          <ThemeToggle />
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Sidebar */}
        {!sidebarCollapsed && (
          <aside className="w-56 border-r border-border bg-sidebar overflow-hidden shrink-0">
            <PageTreeSidebar
              pages={pageEntries}
              activePage={activePageDTag ?? undefined}
              onSelectPage={handleSelectPage}
              onCreatePage={handleCreatePage}
              onDeletePage={handleDeletePage}
              onReorderPages={handleReorderPages}
              pageTitles={pageTitles}
            />
          </aside>
        )}

        {/* Editor */}
        <main className="flex-1 overflow-y-auto">
          {activePageDTag && editorContent ? (
            <div className="max-w-[720px] mx-auto px-10 py-12">
              <input
                type="text"
                value={pageTitle}
                onChange={(e) => { setPageTitle(e.target.value); setDirty(true); }}
                placeholder="Untitled"
                className="w-full text-[2rem] font-bold tracking-[-0.04em] leading-tight bg-transparent border-none outline-none mb-8 placeholder:text-muted-foreground/30"
              />
              <SteleEditor
                content={editorContent}
                onUpdate={handleEditorUpdate}
                placeholder="Write something, or / for blocks…"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-xs font-mono tracking-[0.15em] text-muted-foreground/40 uppercase">
                No page selected
              </p>
            </div>
          )}
        </main>
      </div>

      {spaceEvent && (
        <SpaceSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} spaceEvent={spaceEvent} />
      )}
    </div>
  );
}
